import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { toast } from 'sonner';
import defaultAvatarUrl from '../assets/experts-man-dark.png';

const IN_APP_NOTIFICATION_DURATION_MS = 3000;

export type InAppChatNotificationDetail = {
  type: 'chat_message' | 'unassigned_message';
  conversationId: string;
  senderDisplayName?: string;
  senderPhotoUrl?: string;
  messageText?: string;
};

export const IN_APP_CHAT_NOTIFICATION_EVENT = 'gynger:in-app-chat-notification';

export const IN_APP_CHAT_NOTIFICATION_FEATURE_FLAG = 'in-app-chat-notification';

const MAX_DESCRIPTION_LENGTH = 140;

const truncate = (text: string | undefined, max = MAX_DESCRIPTION_LENGTH) => {
  if (!text) return '';
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * Listens for websocket-driven chat notifications dispatched as
 * `IN_APP_CHAT_NOTIFICATION_EVENT` window events and displays them as a
 * toast banner. Clicking the banner navigates to the corresponding
 * conversation. Notifications are suppressed when the user is already viewing
 * the target conversation.
 *
 * To make the back button always return to the conversations list (even when
 * the user was on an unrelated page like the tracking page or another
 * conversation when they clicked the banner), we first replace the current
 * entry with the messaging home route, then push the conversation route on top.
 */
export default function useInAppChatNotification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<InAppChatNotificationDetail>).detail;
      if (!detail || !detail.conversationId) return;

      if (!posthog.isFeatureEnabled(IN_APP_CHAT_NOTIFICATION_FEATURE_FLAG)) {
        return;
      }

      const currentPath = locationRef.current?.pathname ?? '';
      const match = matchPath(routes.conversation.route, currentPath);
      if (match?.params?.conversationId === detail.conversationId) {
        return;
      }

      const title = detail.senderDisplayName || t('appName');
      const description = truncate(detail.messageText) || t('chats.inAppNotification.defaultBody');
      const key = `chat-${detail.conversationId}`;
      const conversationRoute = routes.conversation.route.replace(':conversationId', detail.conversationId);

      posthog.capture('in_app_notification.shown', {
        notification_type: detail.type,
      });

      const onClick = () => {
        posthog.capture('in_app_notification.clicked', {
          notification_type: detail.type,
        });
        toast.dismiss(key);
        // Ensure the back button from the target conversation always goes
        // back to the conversations list rather than to the page the user
        // was on when the notification arrived.
        const currentPathAtClick = locationRef.current?.pathname ?? '';
        if (currentPathAtClick !== routes.messagingHome.route) {
          navigate(routes.messagingHome.route, { replace: true });
        }
        navigate(conversationRoute);
      };

      toast.custom(
        () => (
          <div
            data-testid="in-app-chat-notification"
            role="status"
            onClick={onClick}
            className="flex w-sm max-w-[calc(100vw-2rem)] cursor-pointer items-center gap-3 rounded-lg border bg-popover p-3 text-popover-foreground shadow-md"
          >
            <img
              className="size-10 shrink-0 rounded-full object-cover"
              src={detail.senderPhotoUrl || defaultAvatarUrl}
              alt={title}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{title}</div>
              <div className="text-sm text-muted-foreground">{description}</div>
            </div>
          </div>
        ),
        { id: key, duration: IN_APP_NOTIFICATION_DURATION_MS, position: 'top-center' },
      );
      // sonner pauses its timer while the pointer rests on the toast; a chat
      // banner should still go away on its own.
      window.setTimeout(() => toast.dismiss(key), IN_APP_NOTIFICATION_DURATION_MS);
    };

    window.addEventListener(IN_APP_CHAT_NOTIFICATION_EVENT, handler);
    return () => {
      window.removeEventListener(IN_APP_CHAT_NOTIFICATION_EVENT, handler);
    };
  }, [navigate, t]);
}
